import React from "react";
import { motion } from "framer-motion";
import {
  Smartphone, Globe, Code, ShoppingCart, Brain, Zap,
  CheckCircle, TrendingUp, BarChart3, Shield, Star, Layers,
  Package, CreditCard, MessageSquare, Eye,
  Apple, Bot, Wallet, Lock, MapPin, Truck, Workflow, Boxes, Sparkles,
  ShieldCheck, Search, FileText, AlertTriangle,
} from "lucide-react";

const phoneBase = {
  initial: { opacity: 0, x: 40 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.8, delay: 0.3, ease: "easeOut" },
};

export function PhoneFrame({ children, accentColor = "#00AEEF" }) {
  return (
    <div className="relative w-full max-w-lg mx-auto">
      <div className="absolute -inset-4 rounded-3xl blur-2xl" style={{ background: `${accentColor}10` }} />
      <div className="relative bg-[#1a1a1a] rounded-[2.5rem] p-3 border border-white/10 shadow-2xl">
        <div className="bg-[#0f0f0f] rounded-[2rem] overflow-hidden">
          <div className="flex items-center justify-between px-6 py-2 bg-[#0f0f0f]">
            <span className="text-[10px] text-gray-400">9:41</span>
            <div className="w-20 h-5 bg-[#1a1a1a] rounded-full" />
            <div className="flex gap-1">
              <div className="w-3 h-2 bg-gray-500 rounded-sm" />
              <div className="w-3 h-2 bg-gray-500 rounded-sm" />
            </div>
          </div>
          {children}
          <div className="flex items-center justify-around px-6 py-3 border-t border-white/5 bg-[#0f0f0f]">
            {[true, false, false, false].map((active, i) => (
              <div key={i} className={`w-5 h-5 rounded-md ${active ? "" : "bg-white/10"}`} style={active ? { backgroundColor: accentColor } : {}} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function FloatingCard({ children, className = "", delay = 1.2 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      className={`absolute bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 shadow-2xl ${className}`}
    >
      {children}
    </motion.div>
  );
}

export function MobileAppHeroVisual() {
  return (
    <motion.div {...phoneBase} className="hidden lg:block relative" data-testid="hero-visual">
      <PhoneFrame accentColor="#00AEEF">
        <div className="px-5 py-4 space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-xl bg-[#00AEEF] flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-white text-sm font-semibold">FitTrack Pro</div>
              <div className="text-gray-500 text-[10px]">Health & Fitness</div>
            </div>
          </div>
          <div className="bg-[#1a1a1a] rounded-xl p-4 border border-white/5">
            <div className="text-[10px] text-gray-500 mb-1">Today's Progress</div>
            <div className="flex items-end gap-1 mt-2">
              <div className="text-white text-2xl font-bold">8,432</div>
              <div className="text-[10px] text-gray-400 mb-1 ml-1">steps</div>
            </div>
            <div className="w-full h-2 bg-[#0f0f0f] rounded-full mt-3">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "72%" }}
                transition={{ delay: 0.8, duration: 1, ease: "easeOut" }}
                className="h-full bg-gradient-to-r from-[#00AEEF] to-[#10B981] rounded-full"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { icon: "flame", val: "486", unit: "cal", color: "#F59E0B" },
              { icon: "heart", val: "72", unit: "bpm", color: "#EC4899" },
              { icon: "moon", val: "7.5", unit: "hrs", color: "#8B5CF6" },
            ].map((m, i) => (
              <div key={i} className="bg-[#1a1a1a] rounded-xl p-3 border border-white/5 text-center">
                <div className="w-2 h-2 rounded-full mx-auto mb-2" style={{ backgroundColor: m.color }} />
                <div className="text-white text-sm font-bold">{m.val}</div>
                <div className="text-gray-500 text-[8px]">{m.unit}</div>
              </div>
            ))}
          </div>
          <div className="bg-[#1a1a1a] rounded-xl p-3 border border-white/5">
            <div className="text-[10px] text-gray-500 mb-2">Weekly Activity</div>
            <div className="flex items-end gap-2 h-16">
              {[50, 70, 40, 85, 60, 90, 45].map((h, i) => (
                <motion.div key={i} initial={{ height: 0 }} animate={{ height: `${h}%` }}
                  transition={{ delay: 0.5 + i * 0.1, duration: 0.6 }}
                  className="flex-1 rounded-md bg-gradient-to-t from-[#00AEEF] to-[#00AEEF]/30"
                />
              ))}
            </div>
          </div>
        </div>
      </PhoneFrame>
      <FloatingCard className="-right-6 top-20" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <Star className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">App Store Rating</div>
            <div className="text-[#F59E0B] text-[10px]">4.9 / 5.0</div>
          </div>
        </div>
      </FloatingCard>
      <FloatingCard className="-left-6 bottom-24" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#00AEEF]/20 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-[#00AEEF]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Downloads</div>
            <div className="text-gray-400 text-[8px]">+2.4M this month</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

export function WebAppHeroVisual() {
  return (
    <motion.div {...phoneBase} className="hidden lg:block relative" data-testid="hero-visual">
      <div className="relative w-full max-w-lg mx-auto">
        <div className="absolute -inset-4 bg-[#10B981]/10 rounded-3xl blur-2xl" />
        <div className="relative bg-[#1a1a1a] rounded-2xl p-2 border border-white/10 shadow-2xl">
          <div className="bg-[#0f0f0f] rounded-xl overflow-hidden">
            {/* Browser bar */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              </div>
              <div className="flex-1 bg-[#1a1a1a] rounded-md px-3 py-1 ml-2">
                <span className="text-[10px] text-gray-500">app.yourplatform.com/dashboard</span>
              </div>
            </div>
            {/* Dashboard content */}
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-[#10B981] flex items-center justify-center">
                    <Globe className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-white text-xs font-semibold">Analytics Dashboard</span>
                </div>
                <div className="text-[8px] text-gray-500 bg-[#1a1a1a] px-2 py-1 rounded">Live</div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "Visitors", val: "24.8K", pct: "+18%", color: "#10B981" },
                  { label: "Revenue", val: "$89.2K", pct: "+24%", color: "#00AEEF" },
                  { label: "Conv. Rate", val: "3.4%", pct: "+0.8%", color: "#8B5CF6" },
                ].map((m, i) => (
                  <div key={i} className="bg-[#1a1a1a] rounded-lg p-2.5 border border-white/5">
                    <div className="text-[8px] text-gray-500">{m.label}</div>
                    <div className="text-white text-sm font-bold mt-1">{m.val}</div>
                    <div className="text-[8px] mt-0.5" style={{ color: m.color }}>{m.pct}</div>
                  </div>
                ))}
              </div>
              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="text-[8px] text-gray-500 mb-2">Traffic Sources</div>
                <div className="space-y-2">
                  {[
                    { name: "Organic", w: "65%", color: "#10B981" },
                    { name: "Direct", w: "45%", color: "#00AEEF" },
                    { name: "Referral", w: "30%", color: "#8B5CF6" },
                  ].map((s, i) => (
                    <div key={i}>
                      <div className="flex justify-between text-[8px] mb-1">
                        <span className="text-gray-400">{s.name}</span>
                        <span className="text-white">{s.w}</span>
                      </div>
                      <div className="h-1.5 bg-[#0f0f0f] rounded-full">
                        <motion.div initial={{ width: 0 }} animate={{ width: s.w }}
                          transition={{ delay: 0.6 + i * 0.2, duration: 0.8 }}
                          className="h-full rounded-full" style={{ backgroundColor: s.color }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <CheckCircle className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Tests Passing</div>
            <div className="text-[#10B981] text-[8px]">247/247 passed</div>
          </div>
        </div>
      </FloatingCard>
      <FloatingCard className="-left-6 bottom-20" delay={1.5}>
        <div className="font-mono text-[10px]">
          <span className="text-[#8B5CF6]">yarn</span>{" "}
          <span className="text-[#10B981]">deploy</span>{" "}
          <span className="text-gray-400">--prod</span>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

export function CustomSoftwareHeroVisual() {
  return (
    <motion.div {...phoneBase} className="hidden lg:block relative" data-testid="hero-visual">
      <div className="relative w-full max-w-lg mx-auto">
        <div className="absolute -inset-4 bg-[#8B5CF6]/10 rounded-3xl blur-2xl" />
        <div className="relative bg-[#1a1a1a] rounded-2xl p-2 border border-white/10 shadow-2xl">
          <div className="bg-[#0f0f0f] rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              </div>
              <div className="flex-1 bg-[#1a1a1a] rounded-md px-3 py-1 ml-2">
                <span className="text-[10px] text-gray-500">enterprise.platform.io</span>
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg bg-[#8B5CF6] flex items-center justify-center">
                  <Layers className="w-4 h-4 text-white" />
                </div>
                <span className="text-white text-xs font-semibold">System Architecture</span>
              </div>
              {/* Microservices diagram */}
              <div className="grid grid-cols-3 gap-2">
                {["Auth Service", "API Gateway", "User Service", "Payment", "Notifications", "Analytics"].map((svc, i) => (
                  <motion.div key={i}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 + i * 0.1 }}
                    className="bg-[#1a1a1a] rounded-lg p-2 border border-white/5 text-center"
                  >
                    <div className="w-3 h-3 rounded mx-auto mb-1" style={{ backgroundColor: ["#8B5CF6", "#00AEEF", "#10B981", "#F59E0B", "#EC4899", "#06B6D4"][i] }} />
                    <div className="text-[7px] text-gray-400">{svc}</div>
                  </motion.div>
                ))}
              </div>
              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="text-[8px] text-gray-500 mb-2">System Health</div>
                <div className="space-y-2">
                  {[
                    { name: "API Uptime", val: "99.99%", color: "#10B981" },
                    { name: "Avg Latency", val: "42ms", color: "#00AEEF" },
                    { name: "Throughput", val: "12.4K rps", color: "#8B5CF6" },
                  ].map((m, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-[8px] text-gray-400">{m.name}</span>
                      <span className="text-[10px] font-medium" style={{ color: m.color }}>{m.val}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="flex items-center justify-between">
                  <span className="text-[8px] text-gray-500">Pipeline Status</span>
                  <span className="text-[8px] text-[#10B981]">All Clear</span>
                </div>
                <div className="flex gap-1 mt-2">
                  {[1,2,3,4,5].map((_, i) => (
                    <motion.div key={i}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1 + i * 0.15 }}
                      className="flex-1 h-1.5 rounded-full bg-[#10B981]"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FloatingCard className="-right-6 top-20" delay={1.3}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#8B5CF6]/20 flex items-center justify-center">
            <Shield className="w-4 h-4 text-[#8B5CF6]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">SOC 2 Compliant</div>
            <div className="text-gray-400 text-[8px]">Audit passed</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

export function EcommerceHeroVisual() {
  return (
    <motion.div {...phoneBase} className="hidden lg:block relative" data-testid="hero-visual">
      <PhoneFrame accentColor="#F59E0B">
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-xl bg-[#F59E0B] flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-white text-sm font-semibold">LuxeStore</div>
              <div className="text-gray-500 text-[10px]">Premium Commerce</div>
            </div>
          </div>
          {/* Product cards */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { name: "Minimal Watch", price: "$299" },
              { name: "Canvas Bag", price: "$149" },
            ].map((p, i) => (
              <div key={i} className="bg-[#1a1a1a] rounded-xl border border-white/5 overflow-hidden">
                <div className="h-20 bg-gradient-to-br from-white/5 to-white/[0.02]" />
                <div className="p-2">
                  <div className="text-[9px] text-gray-400">{p.name}</div>
                  <div className="text-white text-xs font-bold">{p.price}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-[#1a1a1a] rounded-xl p-3 border border-white/5">
            <div className="text-[8px] text-gray-500 mb-2">Today's Sales</div>
            <div className="text-white text-xl font-bold">$12,847</div>
            <div className="text-[#10B981] text-[10px]">+34.2% vs yesterday</div>
            <div className="flex items-end gap-1.5 h-12 mt-3">
              {[30, 45, 35, 60, 50, 75, 55, 80, 65, 90, 70, 85].map((h, i) => (
                <motion.div key={i} initial={{ height: 0 }} animate={{ height: `${h}%` }}
                  transition={{ delay: 0.4 + i * 0.06, duration: 0.5 }}
                  className="flex-1 rounded-sm bg-gradient-to-t from-[#F59E0B] to-[#F59E0B]/30"
                />
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between bg-[#1a1a1a] rounded-xl px-3 py-2.5 border border-white/5">
            <span className="text-gray-400 text-[10px]">Orders today</span>
            <span className="text-white text-xs font-bold">284</span>
          </div>
        </div>
      </PhoneFrame>
      <FloatingCard className="-right-6 top-24" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <CreditCard className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Payment Received</div>
            <div className="text-[#10B981] text-[8px]">+$2,499.00</div>
          </div>
        </div>
      </FloatingCard>
      <FloatingCard className="-left-6 bottom-28" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center">
            <Package className="w-4 h-4 text-[#F59E0B]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Shipped</div>
            <div className="text-gray-400 text-[8px]">42 orders today</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

export function AiMlHeroVisual() {
  return (
    <motion.div {...phoneBase} className="hidden lg:block relative" data-testid="hero-visual">
      <div className="relative w-full max-w-lg mx-auto">
        <div className="absolute -inset-4 bg-[#EC4899]/10 rounded-3xl blur-2xl" />
        <div className="relative bg-[#1a1a1a] rounded-2xl p-2 border border-white/10 shadow-2xl">
          <div className="bg-[#0f0f0f] rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              </div>
              <div className="flex-1 bg-[#1a1a1a] rounded-md px-3 py-1 ml-2">
                <span className="text-[10px] text-gray-500">ai.platform.io/model</span>
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg bg-[#EC4899] flex items-center justify-center">
                  <Brain className="w-4 h-4 text-white" />
                </div>
                <span className="text-white text-xs font-semibold">AI Model Studio</span>
              </div>
              {/* Chat interface */}
              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5 space-y-2">
                <div className="flex gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#00AEEF]/20 flex-shrink-0" />
                  <div className="bg-[#0f0f0f] rounded-lg px-3 py-2 text-[9px] text-gray-300">Analyze Q3 sales data and predict next quarter trends</div>
                </div>
                <div className="flex gap-2 justify-end">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                    className="bg-[#EC4899]/10 rounded-lg px-3 py-2 text-[9px] text-gray-300 border border-[#EC4899]/20"
                  >
                    Q4 forecast: +18.3% growth projected based on seasonal patterns...
                  </motion.div>
                  <div className="w-5 h-5 rounded-full bg-[#EC4899]/20 flex-shrink-0" />
                </div>
              </div>
              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="text-[8px] text-gray-500 mb-2">Model Accuracy</div>
                <div className="text-white text-lg font-bold">97.4%</div>
                <div className="w-full h-2 bg-[#0f0f0f] rounded-full mt-2">
                  <motion.div initial={{ width: 0 }} animate={{ width: "97.4%" }}
                    transition={{ delay: 1, duration: 1.2, ease: "easeOut" }}
                    className="h-full bg-gradient-to-r from-[#EC4899] to-[#8B5CF6] rounded-full"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: "Predictions", val: "1.2M", color: "#EC4899" },
                  { label: "Latency", val: "23ms", color: "#10B981" },
                ].map((m, i) => (
                  <div key={i} className="bg-[#1a1a1a] rounded-lg p-2.5 border border-white/5">
                    <div className="text-[8px] text-gray-500">{m.label}</div>
                    <div className="text-white text-sm font-bold mt-0.5">{m.val}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <FloatingCard className="-right-6 top-20" delay={1.3}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#EC4899]/20 flex items-center justify-center">
            <Eye className="w-4 h-4 text-[#EC4899]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Vision Model</div>
            <div className="text-gray-400 text-[8px]">98.7% accuracy</div>
          </div>
        </div>
      </FloatingCard>
      <FloatingCard className="-left-6 bottom-24" delay={1.6}>
        <div className="font-mono text-[10px]">
          <span className="text-[#EC4899]">model</span>
          <span className="text-white">.</span>
          <span className="text-[#10B981]">predict</span>
          <span className="text-gray-400">(data)</span>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

export function CaseStudiesHeroVisual() {
  return (
    <motion.div {...phoneBase} className="hidden lg:block relative" data-testid="hero-visual">
      <div className="relative w-full max-w-lg mx-auto">
        <div className="absolute -inset-4 bg-[#00AEEF]/10 rounded-3xl blur-2xl" />
        <div className="grid grid-cols-2 gap-3">
          {[
            { name: "AT&T", cat: "Enterprise", color: "#00AEEF" },
            { name: "Microsoft", cat: "MVP", color: "#10B981" },
            { name: "BMW", cat: "Design", color: "#F59E0B" },
            { name: "FiscalNote", cat: "Software", color: "#8B5CF6" },
          ].map((cs, i) => (
            <motion.div
              key={cs.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + i * 0.15, duration: 0.5 }}
              className="bg-[#1a1a1a] border border-white/10 rounded-xl p-5 hover:border-white/20 transition-all"
            >
              <div
                className="w-8 h-8 rounded-lg mb-3 flex items-center justify-center font-bold text-xs text-white"
                style={{ backgroundColor: `${cs.color}20`, color: cs.color }}
              >
                {cs.name
                  .split(" ")
                  .map((part) => part[0])
                  .join("")
                  .slice(0, 3)}
              </div>
              <div className="text-white text-sm font-semibold">{cs.name}</div>
              <div className="text-[10px] mt-1" style={{ color: cs.color }}>{cs.cat}</div>
            </motion.div>
          ))}
        </div>
      </div>
      <FloatingCard className="-right-4 -top-2" delay={1.2}>
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold text-white">500+</span>
          <span className="text-[10px] text-gray-400">Projects<br/>Delivered</span>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

export function ContactHeroVisual() {
  return (
    <motion.div {...phoneBase} className="hidden lg:block relative" data-testid="hero-visual">
      <div className="relative w-full max-w-md mx-auto">
        <div className="absolute -inset-4 bg-[#00AEEF]/10 rounded-3xl blur-2xl" />
        <div className="relative space-y-4">
          {[
            { time: "9:00 AM", text: "Send the repo, the constraint, or the blank page.", from: "team", delay: 0.4 },
            { time: "9:01 AM", text: "We need a mobile app for our logistics platform.", from: "user", delay: 0.8 },
            { time: "9:02 AM", text: "A senior engineer will look at it, and tell you if it shouldn't be an app.", from: "team", delay: 1.2 },
          ].map((msg, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: msg.delay, duration: 0.4 }}
              className={`flex ${msg.from === "user" ? "justify-end" : "justify-start"}`}
            >
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                msg.from === "user"
                  ? "bg-[#00AEEF] text-white"
                  : "bg-[#1a1a1a] border border-white/10 text-gray-300"
              }`}>
                <p className="text-xs leading-relaxed">{msg.text}</p>
                <p className={`text-[8px] mt-1 ${msg.from === "user" ? "text-white/60" : "text-gray-600"}`}>{msg.time}</p>
              </div>
            </motion.div>
          ))}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8 }}
            className="flex justify-start"
          >
            <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl px-4 py-3">
              <div className="flex gap-1">
                <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ repeat: Infinity, duration: 1, delay: 0 }} className="w-2 h-2 rounded-full bg-[#00AEEF]" />
                <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ repeat: Infinity, duration: 1, delay: 0.2 }} className="w-2 h-2 rounded-full bg-[#00AEEF]" />
                <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ repeat: Infinity, duration: 1, delay: 0.4 }} className="w-2 h-2 rounded-full bg-[#00AEEF]" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

/** Native iOS: banking-style phone mock with balance, accounts, and Face ID badge. */
export function IosAppHeroVisual() {
  return (
    <motion.div {...phoneBase} className="hidden lg:block relative" data-testid="hero-visual">
      <PhoneFrame accentColor="#00AEEF">
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center justify-between mb-1">
            <div>
              <div className="text-gray-500 text-[9px]">Good morning</div>
              <div className="text-white text-base font-bold">Sarah</div>
            </div>
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#00AEEF] to-[#8B5CF6]" />
          </div>
          <div className="bg-[#1a1a1a] rounded-2xl p-4 border border-white/5">
            <div className="text-[9px] text-gray-500">Total Balance</div>
            <div className="text-white text-2xl font-bold mt-1">$24,847.92</div>
            <div className="text-[#10B981] text-[10px] mt-0.5">+$1,284.50 this month</div>
            <div className="flex items-end gap-1 h-8 mt-3">
              {[40, 55, 45, 65, 50, 70, 60, 80, 65, 85, 75, 90].map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ height: 0 }}
                  animate={{ height: `${h}%` }}
                  transition={{ delay: 0.4 + i * 0.05, duration: 0.4 }}
                  className="flex-1 rounded-sm bg-gradient-to-t from-[#00AEEF] to-[#8B5CF6]/40"
                />
              ))}
            </div>
          </div>
          <div className="space-y-2">
            {[
              { name: "Checking", bal: "$12,420.00", color: "#00AEEF" },
              { name: "Savings", bal: "$11,835.00", color: "#10B981" },
              { name: "Investment", bal: "$592.92", color: "#F59E0B" },
            ].map((a, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 + i * 0.12 }}
                className="bg-[#1a1a1a] rounded-xl p-3 border border-white/5 flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${a.color}20` }}
                  >
                    <Wallet className="w-3.5 h-3.5" style={{ color: a.color }} />
                  </div>
                  <span className="text-white text-[11px] font-medium">{a.name}</span>
                </div>
                <span className="text-gray-300 text-[11px] font-mono">{a.bal}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </PhoneFrame>
      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#00AEEF]/20 flex items-center justify-center">
            <Apple className="w-4 h-4 text-[#00AEEF]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Swift 6 + SwiftUI</div>
            <div className="text-gray-400 text-[8px]">iOS 18 ready</div>
          </div>
        </div>
      </FloatingCard>
      <FloatingCard className="-left-6 bottom-24" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <Lock className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Face ID</div>
            <div className="text-[#10B981] text-[8px]">Secure Enclave</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/** Native Android: Material 3 fleet/driver app with route preview and stops. */
export function AndroidAppHeroVisual() {
  return (
    <motion.div {...phoneBase} className="hidden lg:block relative" data-testid="hero-visual">
      <PhoneFrame accentColor="#00AEEF">
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#10B981] to-[#00AEEF]" />
              <div>
                <div className="text-white text-xs font-semibold">Carlos M.</div>
                <div className="text-[8px] text-[#10B981]">● On Duty</div>
              </div>
            </div>
            <div className="text-[8px] text-gray-500 bg-[#1a1a1a] px-2 py-1 rounded-full">
              Route #214
            </div>
          </div>
          <div className="bg-[#1a1a1a] rounded-2xl p-3 border border-white/5 relative overflow-hidden">
            <svg viewBox="0 0 200 80" className="absolute inset-0 w-full h-full opacity-30">
              <path
                d="M10,70 Q60,15 120,45 T190,25"
                stroke="#00AEEF"
                strokeWidth="2"
                fill="none"
              />
              <circle cx="10" cy="70" r="3" fill="#10B981" />
              <circle cx="190" cy="25" r="3" fill="#F59E0B" />
            </svg>
            <div className="relative flex items-center justify-between">
              <div>
                <div className="text-[8px] text-gray-500">Next stop</div>
                <div className="text-white text-xs font-bold">Pinecrest Plaza</div>
                <div className="text-[#10B981] text-[10px]">3.2 mi · 8 min</div>
              </div>
              <div className="w-9 h-9 rounded-full bg-[#00AEEF]/20 flex items-center justify-center">
                <MapPin className="w-4 h-4 text-[#00AEEF]" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: "Stops", val: "42" },
              { label: "Done", val: "16" },
              { label: "ETA", val: "5:12 PM" },
            ].map((s, i) => (
              <div
                key={i}
                className="bg-[#1a1a1a] rounded-xl p-2 border border-white/5 text-center"
              >
                <div className="text-white text-sm font-bold">{s.val}</div>
                <div className="text-[8px] text-gray-500">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            {[
              { name: "Acme Corp", time: "9:24 AM", done: true },
              { name: "Pinecrest Plaza", time: "10:05 AM", done: false },
            ].map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 + i * 0.15 }}
                className="bg-[#1a1a1a] rounded-xl p-3 border border-white/5 flex items-center gap-3"
              >
                <div
                  className={`w-5 h-5 rounded-full flex items-center justify-center ${
                    t.done ? "bg-[#10B981]" : "border-2 border-white/20"
                  }`}
                >
                  {t.done && <CheckCircle className="w-3 h-3 text-white" />}
                </div>
                <div className="flex-1">
                  <div className="text-white text-[11px] font-medium">{t.name}</div>
                  <div className="text-gray-500 text-[9px]">{t.time}</div>
                </div>
                <Truck className="w-3.5 h-3.5 text-gray-500" />
              </motion.div>
            ))}
          </div>
        </div>
      </PhoneFrame>
      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <Bot className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Kotlin + Compose</div>
            <div className="text-gray-400 text-[8px]">Material You</div>
          </div>
        </div>
      </FloatingCard>
      <FloatingCard className="-left-6 bottom-24" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#00AEEF]/20 flex items-center justify-center">
            <Workflow className="w-4 h-4 text-[#00AEEF]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Routes optimized</div>
            <div className="text-[#10B981] text-[8px]">−18% drive time</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/** Cross-platform: marketplace phone + shared component snippet with iOS/Android proof. */
export function HybridAppHeroVisual() {
  return (
    <motion.div {...phoneBase} className="hidden lg:block relative" data-testid="hero-visual">
      <PhoneFrame accentColor="#00AEEF">
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#00AEEF] flex items-center justify-center">
                <Layers className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-white text-xs font-semibold">Stack Market</div>
                <div className="text-[8px] text-gray-500">v3.2.0 · Universal</div>
              </div>
            </div>
            <div className="text-[8px] text-[#10B981] bg-[#10B981]/10 border border-[#10B981]/20 px-2 py-1 rounded-full">
              Live
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { name: "Aerial Drone X1", price: "$1,249" },
              { name: "Studio Mic Pro", price: "$329" },
              { name: "Mech Keyboard", price: "$189" },
              { name: "USB-C Hub", price: "$79" },
            ].map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + i * 0.1 }}
                className="bg-[#1a1a1a] rounded-xl border border-white/5 overflow-hidden"
              >
                <div className="h-12 bg-gradient-to-br from-white/5 to-white/[0.02]" />
                <div className="p-2">
                  <div className="text-[9px] text-gray-400 truncate">{p.name}</div>
                  <div className="text-white text-xs font-bold">{p.price}</div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="bg-[#1a1a1a] rounded-xl p-3 border border-white/5 font-mono">
            <div className="flex items-center gap-1.5 mb-2">
              <div className="w-1.5 h-1.5 rounded-full bg-[#00AEEF]" />
              <span className="text-[8px] text-gray-500">
                ProductCard.tsx · shared
              </span>
            </div>
            <div className="space-y-0.5 text-[8px] leading-relaxed">
              <div>
                <span className="text-[#EC4899]">export</span>{" "}
                <span className="text-[#00AEEF]">const</span>{" "}
                <span className="text-white">ProductCard</span>{" "}
                <span className="text-gray-400">{"= () => ("}</span>
              </div>
              <div className="pl-3">
                <span className="text-gray-500">&lt;</span>
                <span className="text-[#10B981]">View</span>
                <span className="text-gray-500">&gt;</span>
                <span className="text-gray-500"> {"{/* iOS + Android */}"}</span>
              </div>
              <div>
                <span className="text-gray-400">{");"}</span>
              </div>
            </div>
          </div>
        </div>
      </PhoneFrame>
      <FloatingCard className="-right-6 top-12" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#00AEEF]/20 flex items-center justify-center">
            <Apple className="w-4 h-4 text-[#00AEEF]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">iOS</div>
            <div className="text-[#10B981] text-[8px]">Shipped</div>
          </div>
        </div>
      </FloatingCard>
      <FloatingCard className="-right-6 bottom-32" delay={1.4}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <Bot className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Android</div>
            <div className="text-[#10B981] text-[8px]">Shipped</div>
          </div>
        </div>
      </FloatingCard>
      <FloatingCard className="-left-6 bottom-20" delay={1.6}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#8B5CF6]/20 flex items-center justify-center">
            <Layers className="w-4 h-4 text-[#8B5CF6]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">1 codebase</div>
            <div className="text-gray-400 text-[8px]">React Native</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/** Low-code / RMAD: drag-and-drop app builder with components, canvas, properties. */
export function LowCodeAppHeroVisual() {
  return (
    <motion.div {...phoneBase} className="hidden lg:block relative" data-testid="hero-visual">
      <div className="relative w-full max-w-lg mx-auto">
        <div className="absolute -inset-4 bg-[#00AEEF]/10 rounded-3xl blur-2xl" />
        <div className="relative bg-[#1a1a1a] rounded-2xl p-2 border border-white/10 shadow-2xl">
          <div className="bg-[#0f0f0f] rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              </div>
              <div className="flex-1 bg-[#1a1a1a] rounded-md px-3 py-1 ml-2">
                <span className="text-[10px] text-gray-500">
                  builder.app/inspections-mobile
                </span>
              </div>
            </div>
            <div className="p-3 flex gap-2">
              <div className="w-24 bg-[#1a1a1a] rounded-lg p-2 border border-white/5 space-y-1.5">
                <div className="text-[8px] text-gray-500 mb-1">Components</div>
                {[
                  { icon: Smartphone, label: "Screen" },
                  { icon: Layers, label: "List" },
                  { icon: Code, label: "Form" },
                  { icon: BarChart3, label: "Chart" },
                  { icon: Workflow, label: "Flow" },
                ].map((c, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + i * 0.08 }}
                    className="flex items-center gap-1.5 bg-[#0f0f0f] rounded-md px-1.5 py-1 border border-white/5"
                  >
                    <c.icon className="w-3 h-3 text-[#00AEEF]" />
                    <span className="text-[8px] text-gray-300">{c.label}</span>
                  </motion.div>
                ))}
              </div>
              <div className="flex-1 bg-[#1a1a1a] rounded-lg p-3 border border-white/5 flex items-center justify-center">
                <div className="w-28 bg-[#0f0f0f] rounded-2xl p-2 border border-white/10">
                  <div className="bg-[#1a1a1a] rounded-xl p-2 space-y-2">
                    <div className="text-[8px] text-white font-semibold">
                      Site Inspection
                    </div>
                    <div className="h-7 bg-gradient-to-br from-[#00AEEF]/30 to-[#00AEEF]/10 rounded-md" />
                    <div className="space-y-1">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="h-1.5 bg-[#0f0f0f] rounded-sm" />
                      ))}
                    </div>
                    <div className="h-5 bg-[#00AEEF] rounded-md flex items-center justify-center">
                      <span className="text-[7px] text-white font-semibold">
                        Submit
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-20 bg-[#1a1a1a] rounded-lg p-2 border border-white/5 space-y-1.5">
                <div className="text-[8px] text-gray-500 mb-1">Properties</div>
                {[
                  { label: "Color", val: "#00AEEF" },
                  { label: "Padding", val: "16" },
                  { label: "Source", val: "API" },
                ].map((p, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 + i * 0.1 }}
                    className="bg-[#0f0f0f] rounded-md px-1.5 py-1 border border-white/5"
                  >
                    <div className="text-[7px] text-gray-500">{p.label}</div>
                    <div className="text-[8px] text-white font-mono">{p.val}</div>
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="px-3 pb-3">
              <div className="w-full h-7 bg-gradient-to-r from-[#00AEEF] to-[#06B6D4] rounded-md flex items-center justify-center gap-1.5">
                <Sparkles className="w-3 h-3 text-white" />
                <span className="text-[10px] text-white font-semibold">
                  Publish to iOS & Android
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FloatingCard className="-right-6 top-20" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <Zap className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Ship in weeks</div>
            <div className="text-[#10B981] text-[8px]">Not months</div>
          </div>
        </div>
      </FloatingCard>
      <FloatingCard className="-left-6 bottom-24" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#8B5CF6]/20 flex items-center justify-center">
            <Boxes className="w-4 h-4 text-[#8B5CF6]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Platform agnostic</div>
            <div className="text-gray-400 text-[8px]">PowerApps · OutSystems</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}

/** Security Audits: audit console showing risk posture, findings by severity, and scan progress. */
export function SecurityAuditHeroVisual() {
  const accent = "#10B981";
  return (
    <motion.div {...phoneBase} className="hidden lg:block relative" data-testid="hero-visual">
      <div className="relative w-full max-w-lg mx-auto">
        <div className="absolute -inset-4 rounded-3xl blur-2xl" style={{ background: `${accent}10` }} />
        <div className="relative bg-[#1a1a1a] rounded-2xl p-2 border border-white/10 shadow-2xl">
          <div className="bg-[#0f0f0f] rounded-xl overflow-hidden">
            {/* Browser chrome */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              </div>
              <div className="flex-1 bg-[#1a1a1a] rounded-md px-3 py-1 ml-2">
                <span className="text-[10px] text-gray-500">audit.maxiom.app/acme-payments</span>
              </div>
            </div>

            <div className="p-4 space-y-3">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: accent }}>
                    <ShieldCheck className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-white text-xs font-semibold">acme-payments</div>
                    <div className="text-[9px] text-gray-500">Tier 3 · commit 4f9c2a1</div>
                  </div>
                </div>
                <div className="text-[8px] px-2 py-1 rounded-full border" style={{ color: accent, backgroundColor: `${accent}15`, borderColor: `${accent}30` }}>
                  In audit
                </div>
              </div>

              {/* Findings by severity */}
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: "Critical", val: "0", color: "#10B981" },
                  { label: "High", val: "2", color: "#EF4444" },
                  { label: "Medium", val: "5", color: "#F59E0B" },
                  { label: "Low", val: "8", color: "#00AEEF" },
                ].map((m, i) => (
                  <div key={i} className="bg-[#1a1a1a] rounded-lg p-2.5 border border-white/5 text-center">
                    <div className="text-white text-base font-bold">{m.val}</div>
                    <div className="text-[8px] mt-0.5" style={{ color: m.color }}>{m.label}</div>
                  </div>
                ))}
              </div>

              {/* Scan progress */}
              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <Search className="w-3 h-3 text-gray-500" />
                    <span className="text-[9px] text-gray-500">Coverage</span>
                  </div>
                  <span className="text-[9px] font-medium" style={{ color: accent }}>78%</span>
                </div>
                <div className="w-full h-2 bg-[#0f0f0f] rounded-full">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: "78%" }}
                    transition={{ delay: 0.8, duration: 1.2, ease: "easeOut" }}
                    className="h-full rounded-full"
                    style={{ background: `linear-gradient(90deg, ${accent}, #00AEEF)` }}
                  />
                </div>
              </div>

              {/* Check list */}
              <div className="bg-[#1a1a1a] rounded-lg p-3 border border-white/5 space-y-2">
                {[
                  { icon: CheckCircle, label: "Auth & session handling", status: "Passed", color: "#10B981" },
                  { icon: AlertTriangle, label: "Payment flow · idempotency", status: "2 findings", color: "#EF4444" },
                  { icon: Lock, label: "Secrets & key management", status: "Passed", color: "#10B981" },
                ].map((r, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 + i * 0.15, duration: 0.4 }}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <r.icon className="w-3 h-3" style={{ color: r.color }} />
                      <span className="text-[9px] text-gray-300">{r.label}</span>
                    </div>
                    <span className="text-[8px] px-1.5 py-0.5 rounded" style={{ color: r.color, backgroundColor: `${r.color}15` }}>
                      {r.status}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <FloatingCard className="-right-6 top-16" delay={1.2}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
            <FileText className="w-4 h-4 text-[#10B981]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Tier assigned</div>
            <div className="text-[#10B981] text-[8px]">T3 · in 72h</div>
          </div>
        </div>
      </FloatingCard>

      <FloatingCard className="-left-6 bottom-24" delay={1.5}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#00AEEF]/20 flex items-center justify-center">
            <Shield className="w-4 h-4 text-[#00AEEF]" />
          </div>
          <div>
            <div className="text-white text-[10px] font-medium">Fixed price</div>
            <div className="text-gray-400 text-[8px]">Known up front</div>
          </div>
        </div>
      </FloatingCard>
    </motion.div>
  );
}
